import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./TrendReport.css";
import { fetchCollections } from "../../redux/reducers/collection";
import { cldUrl } from "../../utils/cloudinary";

const PRIORITY_COUNT = 2; // cards visible on the first screen
const SRCSET_WIDTHS = [240, 360, 480, 640, 800];

// Maps the raw list of fetched Collection docs into display cards.
// Uses collection-level fields (coverImage, slug) — not the products
// nested inside each collection.
function mapCollectionsToItems(collections) {
  if (!collections?.length) return null;
  return collections.map((collection) => ({
    id: collection._id || collection.id,
    label: collection.slug,
    href: collection.slug ? `/collections/${collection.slug}` : "#",
    image:
      collection.coverImage ||
      "https://placehold.co/700x900/e4e4e7/71717a?text=No+Image",
  }));
}

function ChevronIcon() {
  return (
    <svg
      className="trend-report__chevron"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 2.5L8 6L4 9.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function TrendReport({
  slug, // optional: narrow the fetch to one specific collection by slug
  division, // optional: narrow the fetch to a division, e.g. "Women"
  status = "active", // only "active" collections are fetched by default
  subheading = "Shop By Collection",
  title,
  items, // optional: pass explicit items to bypass fetching entirely
}) {
  const dispatch = useDispatch();
  const {
    items: collections,
    loading,
    error,
  } = useSelector((state) => state.collections);

  const fetchParams = {
    limit: 100,
    ...(status && { status }),
    ...(division && { division }),
    ...(slug && { slug }),
  };
  const shouldFetch = Boolean(division || slug || status);

  useEffect(() => {
    if (!shouldFetch || items) return;

    // index.js may have already started this exact request before React
    // mounted. If so, skip the duplicate (once), so later mounts still fetch.
    const key = JSON.stringify(fetchParams);
    if (window.__prefetchedCollections === key) {
      window.__prefetchedCollections = null;
      return;
    }

    dispatch(fetchCollections(fetchParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, division, status, items, dispatch]);

  const fetchedItems = mapCollectionsToItems(collections);
  const resolvedItems = items || fetchedItems || [];

  const isLoading = !items && loading && resolvedItems.length === 0;
  const hasError = !items && error && resolvedItems.length === 0;

  const scrollerRef = useRef(null);
  const [pageCount, setPageCount] = useState(1);
  const [activeIndex, setActiveIndex] = useState(0);

  // A "page" is one scroller-width of cards, so how many dots we need
  // depends on how many cards fit per view, which changes with screen size.
  const recalculatePages = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollWidth, clientWidth } = scroller;
    if (!clientWidth) return;
    setPageCount(Math.max(1, Math.ceil((scrollWidth - 1) / clientWidth)));
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // Fallback for browsers without ResizeObserver
    if (typeof ResizeObserver === "undefined") {
      recalculatePages();
      return;
    }

    const resizeObserver = new ResizeObserver(recalculatePages);
    resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) =>
      resizeObserver.observe(child),
    );
    return () => resizeObserver.disconnect();
  }, [resolvedItems, recalculatePages]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let frame = null;
    const updateActive = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 1 || pageCount <= 1) {
        setActiveIndex(0);
        return;
      }
      const progress = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);
      setActiveIndex(Math.round(progress * (pageCount - 1)));
    };
    const handleScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        updateActive();
      });
    };

    scroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", handleScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [pageCount]);

  const scrollToPage = (index) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    const left = pageCount > 1 ? (index / (pageCount - 1)) * maxScroll : 0;
    scroller.scrollTo({ left, behavior: "smooth" });
  };

  const isEmpty = !loading && !error && resolvedItems.length === 0;
  if (isEmpty) return null;

  return (
    <section className="trend-report" aria-label={title}>
      <div className="trend-report__header-text">
        <p className="trend-report__subheading">{subheading}</p>
        <h2 className="trend-report__heading">{title}</h2>
      </div>

      {hasError ? (
        <div className="trend-report__state trend-report__state--error">
          <p>
            {typeof error === "string"
              ? error
              : "Couldn't load this collection."}
          </p>
          <button
            type="button"
            className="trend-report__retry"
            onClick={() => dispatch(fetchCollections(fetchParams))}
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="trend-report__grid" ref={scrollerRef}>
            {isLoading
              ? Array.from({ length: 4 }, (_, index) => (
                  <div
                    key={index}
                    className="trend-report__card trend-report__card--skeleton"
                    aria-hidden="true"
                  >
                    <div className="trend-report__image-wrap" />
                    <span className="trend-report__caption">&nbsp;</span>
                  </div>
                ))
              : resolvedItems.map((item, index) => {
                  const eager = index < PRIORITY_COUNT;
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      className="trend-report__card"
                    >
                      <div className="trend-report__image-wrap">
                        <img
                          src={cldUrl(item.image, 480)}
                          srcSet={SRCSET_WIDTHS.map(
                            (w) => `${cldUrl(item.image, w)} ${w}w`,
                          ).join(", ")}
                          sizes="(max-width: 768px) 45vw, 25vw"
                          width={700}
                          height={900}
                          alt="" /* the caption below already names the link */
                          className="trend-report__image"
                          loading={eager ? "eager" : "lazy"}
                          fetchPriority={eager ? "high" : "auto"}
                          decoding={eager ? undefined : "async"}
                        />
                      </div>
                      <span className="trend-report__caption">
                        {item.label}
                        <ChevronIcon />
                      </span>
                    </a>
                  );
                })}
          </div>
          <div className="trend-report__dots-slot">
            {!isLoading && pageCount > 1 && (
              <div
                className="trend-report__dots"
                role="tablist"
                aria-label="Slides"
              >
                {Array.from({ length: pageCount }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={index === activeIndex}
                    aria-label={`Go to page ${index + 1} of ${pageCount}`}
                    className={`trend-report__dot${
                      index === activeIndex ? " is-active" : ""
                    }`}
                    onClick={() => scrollToPage(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
